import fareKeyLookup from "./fare_key_lookup";
import Session from "@lib/session";
import { getConfig } from "@lib/use_config";
import { LocationDocument, LocationQuery, LocationQueryVariables } from "@graphql/api";

async function locationLookup(fareCode: string, serviceArea?: number): Promise<be.Location> {
  const client = global.window.apolloClient;
  const queryVars: LocationQueryVariables = {
    fareCode,
    serviceAreaId: serviceArea || Session.getConfig().serviceArea,
  };
  const { data }: { data: LocationQuery } = await client.query({
    query: LocationDocument,
    variables: queryVars,
  });
  return data.location;
}

// If google prediction call google to get details
async function googlePredictionToLocation(
  prediction: be.LocationPrediction,
  noMassaging = false,
): Promise<be.Location> {
  return new Promise(function (resolve) {
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div"),
    );
    placesService.getDetails({ placeId: prediction.key }, (place) => {
      const location: be.Location = {
        googleObj: place,
        googlePlaceId: prediction.key,
        displayAddress: prediction.description,
        displayName: prediction.title,
        typeString: prediction.type,
      };

      // Is this potentially a case where Google's getDetails returned a place that is more
      // general then what the autocomplete result suggested? Do some basic checking/normalizing.
      if (
        !noMassaging &&
        prediction.title.match(/^\d+/) &&
        place?.address_components &&
        !place?.address_components[0].long_name.match(/^\d+/) &&
        place?.address_components[0].types.includes("route")
      ) {
        place.address_components.unshift({
          long_name: String(parseInt(prediction.title)),
          short_name: String(parseInt(prediction.title)),
          types: ["street_number"],
        });
        place.types = ["street_address"];
      }

      if (place?.geometry?.location) {
        location.latLng = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
      }

      resolve(location);
    });
  });
}

interface GetLocationDetailsReturn extends be.FareKeyLookupReturn {
  enhancedPrediction?: be.Location;
}

async function getLocationDetailsForRouteInstead(
  loc: be.Location,
  serviceAreaFilter,
): Promise<GetLocationDetailsReturn> {
  return new Promise(async function (resolve) {
    const streetNumber = loc.googleObj?.address_components?.find((comp) =>
      comp.types.includes("street_number"),
    )?.short_name;

    if (
      streetNumber &&
      (loc.googleObj?.types?.includes("street_address") ||
        loc.googleObj?.types?.includes("premise") ||
        loc.googleObj?.types?.includes("subpremise") ||
        loc.googleObj?.types?.includes("establishment") ||
        loc.googleObj?.types?.includes("point_of_interest")) &&
      loc.displayName
    ) {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
      await placesService.findPlaceFromQuery(
        {
          query: `${loc.displayName.replace(streetNumber, "").trim()}, ${loc.displayAddress}`,
          fields: ["place_id"],
          locationBias: loc.latLng,
        },
        async (place) => {
          if (!place) resolve(null);

          const newPrediction: be.LocationPrediction = {
            key: place[0].place_id,
            description: loc.displayAddress,
            title: loc.displayName,
            type: loc.typeString,
          };

          const newLoc = await googlePredictionToLocation(newPrediction, true);
          const result = await fareKeyLookup(newLoc, serviceAreaFilter);

          if (!result.error && result.lookupType != 20) {
            if (result.location?.googleObj) result.location.googleObj.secondChanceFkl = true;
          }
          resolve(result);
        },
      );
    } else {
      resolve(null);
    }
  });
}

export async function getLocationDetails(
  prediction: be.LocationPrediction,
  serviceAreaFilter?: number | number[],
): Promise<GetLocationDetailsReturn> {
  let result: GetLocationDetailsReturn;

  let loc;
  if (prediction.source == "google") {
    loc = await googlePredictionToLocation(prediction);
    result = await fareKeyLookup(loc, serviceAreaFilter);

    // If fare key lookup failed and this location has a street number, try to look it up
    // without the street number (if routes are allowed).
    if (result.error) {
      let routeResult = await getLocationDetailsForRouteInstead(loc, serviceAreaFilter);
      if (routeResult) result = routeResult;
    }

    // FKL sometimes doesn't return a complete street address. If we have a street number and
    // the address does not appear to have it then add it in.
    const googleStreetNumber = loc.googleObj?.address_components?.find((comp) =>
      comp.types.includes("street_number"),
    )?.short_name;
    if (
      result.location?.type == "P" &&
      googleStreetNumber &&
      !result.location?.address?.match(new RegExp("\\b" + googleStreetNumber + "\\b"))
    ) {
      result.location.address = `${googleStreetNumber} ${result.location.address}`;
    }

    result.enhancedPrediction = {
      ...loc,
      fklRequest: result.fklRequest,
      fklResponse: result.fklResponse,
    };
  } else {
    // Service area could be an array.
    const serviceArea = Array.isArray(prediction.servicearea)
      ? prediction.servicearea[0]
      : prediction.servicearea;

    const fareCode = prediction.farecode || String(prediction.key).split(".").at(-1);
    loc = await locationLookup(String(fareCode), serviceArea);

    if (prediction.partiallocation) {
      // Any filled-in fields from the partial location should override the base record.
      Object.entries(prediction.partiallocation).forEach((entry) => {
        const [key, value] = entry;
        if (value === null || value == "") return;
        loc = { ...loc, [key]: value };
      });
    }

    loc = { ...loc, serviceArea: prediction.servicearea || Session.getConfig().serviceArea };

    // Couldn't match the location? Add the display info so the user can see what they selected.
    if (!loc.code) {
      loc = { ...loc, displayName: prediction.title, displayAddress: prediction.description };
    }

    // NOTE: If I don't set google place id and type to an empty string
    // then react-hook-form will use whatever value may have already been set
    // for the previous location.
    result = { location: { ...loc, googlePlaceId: "" } };
  }

  return result;
}
